import { type ReactNode, useState } from 'react'
import { Check, Copy, Expand } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export interface DataTableColumn<T> {
  header: string
  /** Alineación de la celda. Default: 'left'. */
  align?: 'left' | 'right'
  cell: (row: T, index: number) => ReactNode
  /** Valor en texto plano para copiar; por defecto usa `cell` si retorna texto/número. */
  copyValue?: (row: T, index: number) => string | number
}

interface Props<T> {
  /** Título mostrado en el encabezado del modal. */
  title: string
  columns: DataTableColumn<T>[]
  rows: T[]
}

/** Convierte el contenido de una celda a texto plano (para copiar). */
function cellToText(value: ReactNode): string {
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  return ''
}

/**
 * Copia texto al portapapeles. Igual que link-tracker pero con la Clipboard API
 * PRIMERO: dentro del modal, el focus trap de Radix invalida la selección del
 * textarea, por lo que `execCommand('copy')` copiaría vacío. Solo se usa el
 * fallback de textarea si la Clipboard API no está disponible o falla.
 */
async function copiarAlPortapapeles(texto: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(texto)
      return true
    } catch {
      // continúa al fallback
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = texto
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '0'
  textarea.setAttribute('readonly', '')
  document.body.appendChild(textarea)
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)

  let success = false
  try {
    success = document.execCommand('copy')
  } catch {
    success = false
  }
  document.body.removeChild(textarea)
  return success
}

/**
 * Botones compactos (esquina superior derecha de una gráfica): copiar los datos
 * y abrir un modal con una tabla que renderiza todas las filas que la pueblan.
 */
export function ChartDataTableDialog<T>({ title, columns, rows }: Props<T>) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    // TSV (separado por tabuladores) para pegar limpio en Excel / Google Sheets.
    const encabezado = columns.map((col) => col.header).join('\t')
    const filas = rows.map((row, ri) =>
      columns
        .map((col) =>
          String(col.copyValue ? col.copyValue(row, ri) : cellToText(col.cell(row, ri))),
        )
        .join('\t'),
    )
    const tsv = [encabezado, ...filas].join('\n')

    const success = await copiarAlPortapapeles(tsv)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('Copiado al portapapeles')
    } else {
      toast.error('Error al copiar')
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
        aria-label="Ver datos en tabla"
        title="Ver datos en tabla"
        onClick={() => setOpen(true)}
      >
        <Expand className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle className="text-base">{title}</DialogTitle>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                aria-label="Copiar datos"
                title="Copiar datos"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-teal-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </DialogHeader>
          <div className="-mx-6 overflow-y-auto px-6">
            <Table className="text-xs [&_th]:h-8 [&_td]:py-1.5">
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow>
                  {columns.map((col, i) => (
                    <TableHead
                      key={i}
                      className={col.align === 'right' ? 'text-right' : undefined}
                    >
                      {col.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, ri) => (
                  <TableRow key={ri}>
                    {columns.map((col, ci) => (
                      <TableCell
                        key={ci}
                        className={
                          col.align === 'right' ? 'text-right tabular-nums' : undefined
                        }
                      >
                        {col.cell(row, ri)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
